import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { ContentListRow, Page, WireContentListRow } from '../_shared/types';

/**
 * The content list — **every title in every publish state**, which is what makes
 * this the one surface an operator can find a draft on. A draft does not appear
 * in public search at all, which is why the catalogue's own search cannot answer
 * the question this list's field answers.
 *
 * The filter tuple is part of the query key, so each facet combination caches on
 * its own and returning to a previous filter does not re-fetch what is already
 * held.
 */

export interface ContentListFilter {
  /** A `PublishState` member, or `null` for «الكلّ». */
  state: string | null;
  /** A `CONTENT_TYPES` member, or `null` for «النوعان». */
  contentType: string | null;
  /** The operator's search term, or the empty string when the field is empty. */
  term: string;
  /** One-based. Part of the tuple, so each page caches on its own. */
  page: number;
}

export function adminItemsKey(filter?: ContentListFilter) {
  return filter
    ? key('content', 'adminItems', filter.state, filter.contentType, filter.term, filter.page)
    : key('content', 'adminItems');
}

/**
 * **The facets are the service's and so is the paging.** Unlike the editorial
 * list, every narrowing on this surface is already a wire parameter — so a page
 * here is a page of *the filtered list*, and the pager's figures describe the
 * same set the table shows. Page one of «مسودّة» is the first page of drafts,
 * not the drafts that happened to land on the first page of everything.
 *
 * **No `page_size` is sent.** The page size is the service's, and a number
 * chosen here would put a server-owned value behind a rebuild of this bundle.
 */
export function useAdminContentItems(filter: ContentListFilter) {
  return useQuery({
    queryKey: adminItemsKey(filter),
    queryFn: () => {
      const params = new URLSearchParams();
      // `publish_state` is the parameter the service registers. The console used
      // to send `state`, which FastAPI ignores as an unknown query parameter —
      // so the facet narrowed nothing and every chip returned the whole list.
      if (filter.state) params.set('publish_state', filter.state);
      if (filter.contentType) params.set('contentType', filter.contentType);
      // `q` is the same parameter the pickers send, and it is what makes this
      // list searchable at all: a term applied to the twenty rows already
      // fetched would answer «لا عنوان يطابق…» about titles that plainly exist
      // on page four. The service folds it against `title_normalized`, so `أحمد`
      // finds `احمد`.
      const term = filter.term.trim();
      if (term) params.set('q', term);
      if (filter.page > 1) params.set('current_page', String(filter.page));

      const suffix = params.toString();
      return api
        .get<Page<WireContentListRow>>(
          'content',
          suffix ? `/admin/contentItems?${suffix}` : '/admin/contentItems',
        )
        .then((page): Page<ContentListRow> => ({ ...page, items: page.items.map(toContentListRow) }));
    },
    // A queue's depth is what an operator came back to the tab for.
    staleTime: STALE_TIME.list,
    // Walking a page — or applying a facet — keeps the rows on screen while the
    // next set loads, so the table does not blink through a skeleton.
    placeholderData: keepPreviousData,
  });
}

/**
 * The wire row, under the names the list draws it by.
 *
 * **This seam is why the list printed «ليس رقمًا حلقة · NaN:NaN» under every
 * title.** The service spells the state `publish_state`, publishes two date
 * columns rather than one, and states a runtime in SECONDS; the console read
 * `state`, `dated_at` and `duration_ms`. Every one of those resolved to
 * `undefined`, nothing threw at the seam, and the formatters below it formatted
 * the absence instead of refusing it. One translator, at the one place the wire
 * enters — the same shape `_shared/queries.ts` gives the authoring read.
 *
 * `published_at` and **no fallback**: a draft written and
 * never dated has no date, and the cell says so rather than showing the day it
 * happened to be created as if it were a publication.
 *
 * The counts are floored at zero rather than trusted. They are annotated onto
 * the page by a second query on the owning side, and a row that query missed
 * arrives without them — which is exactly the state that produced `NaN` before.
 */
function toContentListRow(row: WireContentListRow): ContentListRow {
  return {
    id: row.id,
    title: row.title,
    content_type: row.content_type,
    state: row.publish_state,
    has_playable_asset: Boolean(row.has_playable_asset),
    category_name: row.category_name ?? null,
    dated_at: row.published_at ?? null,
    group_count: count(row.group_count),
    asset_count: count(row.asset_count),
    duration_ms: Number.isFinite(row.duration_seconds) ? (row.duration_seconds as number) * 1000 : null,
  };
}

/** A count the wire did not carry is zero, never `undefined` — the console prints it. */
function count(value: number | null | undefined): number {
  return Number.isFinite(value) ? (value as number) : 0;
}
